import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import {
  IQueryParams,
  createEntitySlice,
  EntityState,
  serializeAxiosError,
  IOption,
  BaseResponseEntities,
} from 'app/shared/reducers/reducer.utils';
import { apiOrg } from 'app/config/axios-interceptor';
import { IPerson, defaultValue } from 'app/shared/model/person.model';

const initialState: EntityState<IPerson> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/org/person';

// Actions

export const getEntities = createAsyncThunk(
  'person/fetch_entity_list',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOrg.get<BaseResponseEntities<IPerson>>(apiUrl, {
      params: {
        searchText,
        pageNum: page,
        pageSize: size,
        filters: filters && JSON.stringify(filters),
        sort: sort && sort,
        // cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getAllEntities = createAsyncThunk(
  'person/fetch_all_entity_list',
  async (id?: string) => {
    if (id) return apiOrg.get<IOption[]>(`${apiUrl}/all?id=${id}`);
    return apiOrg.get<IOption[]>(`${apiUrl}/all`);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'person/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return apiOrg.get<IPerson>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'person/create_entity',
  async (entity: IPerson, thunkAPI) => {
    const result = await apiOrg.post<IPerson>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'person/update_entity',
  async (entity: IPerson, thunkAPI) => {
    const result = await apiOrg.put<IPerson>(`${apiUrl}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'person/partial_update_entity',
  async (entity: IPerson, thunkAPI) => {
    const result = await apiOrg.patch<IPerson>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'person/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await apiOrg.delete<IPerson>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const PersonSlice = createEntitySlice({
  name: 'person',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.items,
          totalItems: data.totalRecord,
        };
      })
      .addMatcher(isFulfilled(getAllEntities), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          options: data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = PersonSlice.actions;

// Reducer
export default PersonSlice.reducer;
